// src/lib/accueil-api.js — LES LECTURES DE L'ACCUEIL.
//
// ⚠️ TOUT PART APRÈS L'AFFICHAGE (exigence Dimitri 2026-08-25). L'accueil
// n'ajoute RIEN aux 18 requêtes de la connexion : l'écran se dessine d'abord,
// ces lectures partent ensuite, et il MONTRE SON ATTENTE.
//
// ⚠️ CHAQUE SOURCE EST INDÉPENDANTE ET DIT SON PROPRE ÉCHEC. Une panne des
// messages ne doit pas vider « ce qui vous attend », et surtout : une carte
// vide doit pouvoir dire POURQUOI elle est vide — « rien à traiter » n'est pas
// la même phrase que « la lecture a échoué ». C'est la règle de la refonte :
// jamais un vide muet, jamais un « tout va bien » posé sur une requête ratée.
// D'où la forme { ok, data, motif } par source, jamais un seul try/catch global.
//
// ⚠️ AUCUNE FEUILLE DE DÉTAIL N'EST PRÉ-CHARGÉE ICI. Les trois appels d'une
// feuille partent au clic — voir `chargerFeuilleMenage`.
//
// ⚠️ LES SOURCES SONT CELLES DES ÉCRANS EXISTANTS, jamais des copies :
//   · les ménages          → `menage_runs` (RLS admin_all), fenêtre J-2..J
//   · les contrôles        → `chargerControlesParRun` (le batch du Suivi)
//   · les arrivées/départs → `getTodayMovements` (LE calcul de Calendrier >
//                            Aujourd'hui) — même chiffre des deux côtés
//   · les demandes d'horaire → l'action serveur de l'écran « Demandes »
//   · les incidents        → `listIncidents` + `needsEtatFinancierQualif`
//   · les signalements voyageur → action serveur `signalements_voyageur_list`
//                            (les deux tables sont fermées au navigateur —
//                            une requête directe rendrait zéro EN SILENCE)
// Recopier l'une de ces règles ici, c'est la voir diverger au premier ajustement.

import { chargerControlesParRun } from './controles-suivi-api'
import { listIncidents } from './incidents-api'
import { echeanceAircover } from './accueil-incident'
import { buildMovementsFromSources, getTodayMovements } from '../tabs/calendar/_today-helpers'

/** Le résultat d'une source : sa donnée, ou la RAISON de son absence. */
const ok = (data) => ({ ok: true, data, motif: null })
const ko = (motif) => ({ ok: false, data: null, motif: motif || 'Lecture impossible' })

/** Date civile ISO + delta jours (arithmétique UTC pure — pattern MenagesOpsTab). */
export function decalerJourIso(iso, delta) {
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10)
}

// ⚠️ LA FENÊTRE DES MÉNAGES = J-2..J, ET ELLE A DEUX USAGES DISTINCTS :
//   · « ménages aujourd'hui » ne compte QUE le jour même ;
//   · « un contrôle sans personne » regarde en arrière, parce que la fenêtre
//     de contrôle vit 48 h après le ménage (verrou ADR-207). S'arrêter à
//     aujourd'hui, c'est perdre les contrôles d'hier qui expirent ce soir.
export const JOURS_EN_ARRIERE = 2

const COLS_RUNS = 'id, logement_id, scheduled_date, status, assigned_provider_email, '
  + 'controleur_designe_email, completed_at, guest_count, items_checked, photos, depart_reporte_au'

/** Les ménages de la fenêtre + les contrôles de ces ménages (2 requêtes). */
export async function chargerMenages(supabase, todayIso) {
  try {
    const { data, error } = await supabase
      .from('menage_runs')
      .select(COLS_RUNS)
      .gte('scheduled_date', decalerJourIso(todayIso, -JOURS_EN_ARRIERE))
      .lte('scheduled_date', todayIso)
    if (error) throw error
    const runs = data || []
    // Le batch des contrôles part APRÈS les runs (règle du guichet : ce qui
    // dépend d'une donnée asynchrone se calcule quand elle est là).
    const controles = await chargerControlesParRun(supabase, runs.map((r) => r.id))
    return ok({ runs, controles })
  } catch (e) {
    console.error('[accueil] ménages', e?.message || e)
    return ko('La lecture des ménages a échoué')
  }
}

const COLS_RESAS = 'id, logement_id, start_date, end_date, status, source, '
  + 'guest_first_name, guest_last_name, arrival_time, guest_arrival_time, guest_departure_time'

/**
 * Les mouvements du jour — arrivées et départs.
 *
 * ⚠️ MÊME CALCUL QUE CALENDRIER > AUJOURD'HUI (`getTodayMovements`) : c'est ce
 * qui garantit que le chiffre du bandeau et l'écran de destination disent la
 * même chose. Les blocs « résa directe » viennent de `blocksByMonth`, DÉJÀ
 * chargé par App.jsx pour l'admin → zéro requête de plus pour eux.
 */
export async function chargerMouvements(supabase, todayIso, blocsDirects) {
  try {
    const { data, error } = await supabase
      .from('reservations')
      .select(COLS_RESAS)
      // Un mouvement aujourd'hui = arrivée CE jour ou départ CE jour. La borne
      // est volontairement étroite : l'accueil ne charge pas un mois.
      .or(`start_date.eq.${todayIso},end_date.eq.${todayIso}`)
    if (error) throw error
    const mouvements = buildMovementsFromSources(data || [], blocsDirects || [])
    return ok(getTodayMovements(mouvements, todayIso))
  } catch (e) {
    console.error('[accueil] mouvements', e?.message || e)
    return ko('La lecture des arrivées et départs a échoué')
  }
}

/** Les demandes d'horaire en attente — l'action de l'écran « Demandes ». */
export async function chargerDemandesHoraire(postAction) {
  try {
    const json = await postAction({ action: 'services_orchestrateur_hourly_list' })
    // `service_actif: false` n'est PAS une panne : c'est une cause NOMMÉE —
    // aucun logement n'a le service allumé. La carte doit pouvoir le dire au
    // lieu d'afficher un zéro qui ressemble à « tout est traité ».
    return ok({
      pending: Array.isArray(json?.pending) ? json.pending : [],
      logements: (json && typeof json.logements === 'object' && json.logements) || {},
      serviceActif: json?.service_actif !== false,
    })
  } catch (e) {
    console.error('[accueil] demandes horaire', e?.message || e)
    return ko("La lecture des demandes d'horaire a échoué")
  }
}

/**
 * Les incidents ouverts — la table est VIDE en prod, et c'est une réponse.
 *
 * ⚠️ L'ÉCHÉANCE AIRCOVER EST CALCULÉE ICI, JAMAIS STOCKÉE (spec Dimitri
 * 2026-08-26) : 14 jours après le départ du voyageur OU l'arrivée du suivant,
 * au premier des deux. Deux lectures de plus, SEULEMENT quand une demande
 * AirCover est ouverte — sinon zéro coût. Best-effort : sans résa rattachée,
 * pas d'échéance, pas d'invention.
 */
export async function chargerIncidents(supabase, todayIso) {
  try {
    const incidents = await listIncidents({ limit: 100 })
    const enAttente = incidents.filter((i) => (Array.isArray(i.degradations) ? i.degradations : [])
      .some((d) => d && (d.aircoverDemande === true
        || d.etatFinancier === 'Remboursé AirCover' || d.etatFinancier === 'AirCover + refacturé')
        && !(parseFloat(d.aircoverMontant) > 0) && !(parseFloat(d.aircoverFbz) > 0)))
    if (!supabase || !enAttente.length) return ok(incidents)

    const resaIds = [...new Set(enAttente.map((i) => i.reservation_id).filter(Boolean))]
    const parResa = {}
    if (resaIds.length) {
      const { data } = await supabase.from('reservations')
        .select('id, logement_id, end_date').in('id', resaIds)
      for (const r of data || []) parResa[r.id] = r
    }
    const logements = [...new Set(Object.values(parResa).map((r) => r.logement_id))]
    const arrivees = {}
    if (logements.length) {
      const { data } = await supabase.from('reservations')
        .select('logement_id, start_date, guest_first_name, status')
        .in('logement_id', logements)
        .gte('start_date', todayIso)
        .order('start_date', { ascending: true })
      for (const r of data || []) {
        if (r.status === 'cancelled') continue
        if (!arrivees[r.logement_id]) arrivees[r.logement_id] = r
      }
    }
    for (const i of enAttente) {
      const resa = parResa[i.reservation_id]
      if (!resa) continue
      const proch = arrivees[resa.logement_id]
      const e = echeanceAircover({
        departVoyageur: resa.end_date,
        prochaineArrivee: proch ? proch.start_date : null,
        prochainVoyageur: proch ? proch.guest_first_name : null,
        todayIso,
      })
      if (e) { i._echeanceAircover = e.phrase; i._echeanceTon = e.ton }
    }
    return ok(incidents)
  } catch (e) {
    console.error('[accueil] incidents', e?.message || e)
    return ko('La lecture des incidents a échoué')
  }
}

// ⚠️ « SANS RÉPONSE » N'EST PAS « NON LU ». Un fil est en attente quand son
// DERNIER message est entrant : on a reçu et on n'a pas répondu. Le compteur
// de la barre (`inbox_messages` non lus) répond à une autre question, et les
// deux peuvent légitimement différer — ce n'est pas une divergence.
//
// ⚠️ CE QUI N'EXISTE PAS EN BASE, ET QUE L'ÉCRAN NE MONTRE DONC PAS : le
// jugement d'urgence de l'opérateur, et la réponse préparée. La maquette les
// porte ; aucune colonne ne les tient. Les inventer aurait été le pire cas de
// « tout va bien » posé sur du vide. Ils reviendront avec l'opérateur.
// `pricing_block_id` : la bulle d'un signalement de séjour DIRECT est rattachée
// au bloc, pas à une réservation — sans cette colonne, « Répondre » depuis la
// feuille signalement ne retrouverait jamais le fil d'un voyageur direct.
// `envoi_statut` (N1 revue 29/09) : seule la clé d'état d'envoi de `metadata`
// — une réponse réservée mais pas partie ne referme pas le fil.
const COLS_MSG = 'id, thread_id, direction, canal, logement_id, reservation_id, pricing_block_id, sender_name, sender_email, subject, body, created_at, envoi_statut:metadata->>envoi_statut'
const PLAFOND_MSG = 200

export async function chargerMessages(supabase) {
  try {
    const { data, error } = await supabase
      .from('inbox_messages')
      .select(COLS_MSG)
      .order('created_at', { ascending: false })
      .limit(PLAFOND_MSG)
    if (error) throw error
    return ok(data || [])
  } catch (e) {
    console.error('[accueil] messages', e?.message || e)
    return ko('La lecture des messages a échoué')
  }
}

/**
 * CE QU'UN VOYAGEUR NOUS A DIT — le séjour ET l'état des lieux d'arrivée.
 *
 * ⚠️ CETTE LECTURE PASSE PAR LE SERVEUR, ET ELLE NE PEUT PAS FAIRE AUTREMENT :
 * les deux tables sont fermées au navigateur (RLS service_role — c'est du texte
 * libre écrit par un voyageur, donc de la donnée personnelle). Une requête
 * Supabase directe depuis l'admin renverrait zéro ligne EN SILENCE : un
 * « rien à signaler » parfaitement faux.
 *
 * ⚠️ « PRÉPARÉ, EN ATTENTE » EST UNE RÉPONSE, PAS UNE PANNE. Tant que la
 * migration n'est pas appliquée, le serveur répond `actif:false` — on le
 * remonte tel quel (`actif` + `motif`) pour que l'écran puisse le DIRE, au lieu
 * d'afficher un zéro qui ressemblerait à « tout est traité ».
 */
export async function chargerSignalementsVoyageur(postAction) {
  try {
    const json = await postAction({ action: 'signalements_voyageur_list' })
    const actif = json?.actif !== false
    return {
      ok: true,
      data: {
        signalements: Array.isArray(json?.signalements) ? json.signalements : [],
        actif,
        // Le motif est NOMMÉ : « la mise en base n'est pas appliquée » n'est
        // pas la même phrase que « la lecture a échoué ».
        motif: actif
          ? null
          : json?.raison === 'migration_absente'
            ? "Les signalements des voyageurs ne sont pas encore dans cet écran : leur mise en base n'est pas appliquée."
            : "Les signalements des voyageurs ne sont pas lisibles pour l'instant.",
      },
      motif: null,
    }
  } catch (e) {
    console.error('[accueil] signalements voyageur', e?.message || e)
    return ko('La lecture des signalements voyageur a échoué')
  }
}

/**
 * CE QUE L'OPÉRATEUR A PRÉPARÉ (lot 1 opérateur, Q9) — la file des `proposee`.
 *
 * ⚠️ PASSE PAR LE SERVEUR, obligatoirement : la table est fermée au navigateur
 * en écriture, et la liste est servie AVEC le plafond du jour (Q5) dérivé de
 * la même table que le job — l'écran des décisions et l'accueil lisent le
 * MÊME compte.
 *
 * ⚠️ « PRÉPARÉ, EN ATTENTE » EST UNE RÉPONSE, PAS UNE PANNE : tant que la
 * migration n'est pas appliquée, le serveur répond `actif:false` — remonté tel
 * quel, pour que l'écran puisse le DIRE au lieu d'un zéro qui ressemblerait à
 * « rien à décider ».
 */
/**
 * LES VEILLES DE L'OPÉRATEUR — « ce qui manque, ce qui ne se passe pas ».
 *
 * ⚠️ FAIL-SOFT MAIS JAMAIS MUET. Une lecture qui échoue rend `ok:false` avec
 * son motif : l'accueil le DIT en bas d'écran plutôt que d'afficher une liste
 * amputée qui ressemblerait à « tout va bien ». Même doctrine que les
 * signalements et que l'opérateur dormant.
 *
 * ⚠️ Une vue INCOMPLÈTE (une table illisible côté serveur) n'est pas un succès
 * silencieux non plus : le motif le dit, et le compte reste un minimum.
 */
export async function chargerVeillesOperateur(postAction) {
  try {
    // L'action se route sur `?action=` avant le tri par méthode : le POST
    // du patron d'accueil convient, le handler ne lit aucun corps.
    const json = await postAction({ action: 'veille_manques' })
    const complet = json?.complet !== false
    return {
      ok: true,
      data: {
        alertes: Array.isArray(json?.alertes) ? json.alertes : [],
        nbTues: Number(json?.nb_tues) || 0,
        complet,
      },
      motif: complet
        ? null
        : `Veille incomplète — source(s) illisible(s) : ${(json?.sources_illisibles || []).join(', ')}. Le compte est un minimum.`,
    }
  } catch (e) {
    console.error('[accueil] veilles opérateur', e?.message || e)
    return ko("La veille de l'opérateur n'a pas pu être lue")
  }
}

export async function chargerPropositionsOperateur(postAction) {
  try {
    const json = await postAction({ action: 'operateur_propositions_list' })
    const actif = json?.actif !== false
    return {
      ok: true,
      data: {
        propositions: Array.isArray(json?.propositions) ? json.propositions : [],
        actif,
        plafond: json?.plafond || null,
        motif: actif
          ? null
          : "L'opérateur n'est pas encore en service : sa mise en base n'est pas appliquée.",
      },
      motif: null,
    }
  } catch (e) {
    console.error('[accueil] propositions opérateur', e?.message || e)
    return ko("La lecture des propositions de l'opérateur a échoué")
  }
}

/** Les tâches admin non archivées — la source de l'écran « Mes tâches ». */
export async function chargerTaches(supabase) {
  try {
    const { data, error } = await supabase
      .from('admin_tasks')
      .select('id, title, status, due_at, archived_at, scope, logement_id')
      .is('archived_at', null)
    if (error) throw error
    return ok(data || [])
  } catch (e) {
    console.error('[accueil] tâches', e?.message || e)
    return ko('La lecture des tâches a échoué')
  }
}

/**
 * La prospection — deux nombres, pas trois mille lignes.
 *
 * ⚠️ COÛT SIGNALÉ À DIMITRI (§8.2 du cadrage) : la table `prospects` est
 * FERMÉE au client (RLS service-role, PII) — le navigateur ne peut pas la
 * compter. L'action `count` de /api/prospects fait le comptage côté serveur et
 * ne renvoie que les deux nombres. LOT 4 OPÉRATEUR B : le second nombre est
 * « à regarder » (pas_contacte non masquée), miroir du compteur de la bascule
 * Logements › À démarcher.
 */
export async function chargerProspection(postApi) {
  try {
    const json = await postApi()
    return ok({
      total: Number(json?.total) || 0,
      aRegarder: Number(json?.aRegarder) || 0,
    })
  } catch (e) {
    console.error('[accueil] prospection', e?.message || e)
    return ko('La lecture des prospects a échoué')
  }
}

/**
 * L'ANNUAIRE DES AGENTS (correction 27/08) — les comptes ménage/orchestrateur
 * de l'admin, pour que la désignation propose AUSSI un agent sans ménage
 * récent (nouveau, ou de retour de congé). Fail-soft : sans annuaire, le
 * roster dérivé des runs reste la base — jamais un blocage d'écran.
 */
export async function chargerAnnuaireAgents(supabase) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return []
    const res = await fetch('/api/admin-users', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) return []
    return (json.users || [])
      .filter((u) => ['menage', 'orchestrateur'].includes(u?.app_metadata?.role || u?.role))
      .map((u) => u.email)
      .filter(Boolean)
  } catch (e) {
    console.error('[accueil] annuaire', e?.message || e)
    return []
  }
}

/**
 * CE QU'IL ANNONCE — les événements depuis la dernière fois qu'on a regardé.
 *
 * ⚠️ LE CURSEUR VIT DANS LE NAVIGATEUR, et c'est voulu : « depuis la dernière
 * fois » n'a de sens que pour CETTE personne devant CET écran. Le stocker en
 * base en ferait une vérité partagée qui se décalerait au premier autre
 * appareil — et un événement déjà vu ici disparaîtrait là-bas sans être lu.
 *
 * ⚠️ ET IL EST BORNÉ À 48 H DE RATTRAPAGE. Sans borne, un retour de vacances
 * ferait tomber trois semaines d'annonces d'un coup : ce ne serait plus un fil,
 * ce serait une archive — et Dimitri ne la lirait pas.
 *
 * ⚠️ FAIL-SOFT MAIS JAMAIS MUET : une lecture en échec rend `ok:false` avec son
 * motif. Un fil vide obtenu sur une requête en panne se lirait « il ne s'est
 * rien passé » — exactement le mensonge tranquille que ce dépôt traque.
 */
export const CLE_CURSEUR_EVENEMENTS = 'fbz-evenements-vu'
export const RATTRAPAGE_MS = 48 * 60 * 60 * 1000

export function curseurEvenements(now = Date.now()) {
  let brut = null
  try {
    brut = window.localStorage.getItem(CLE_CURSEUR_EVENEMENTS)
  } catch {
    /* navigation privée, site data bloqué : on retombe sur le rattrapage */
  }
  const t = brut ? Date.parse(brut) : NaN
  const plancher = now - RATTRAPAGE_MS
  if (!Number.isFinite(t) || t < plancher) return new Date(plancher).toISOString()
  return new Date(t).toISOString()
}

export function poserCurseurEvenements(iso) {
  try {
    if (iso) window.localStorage.setItem(CLE_CURSEUR_EVENEMENTS, iso)
  } catch {
    /* sans mémoire locale, le rattrapage de 48 h fait le travail */
  }
}

export async function chargerEvenements(postAction, depuis) {
  try {
    const json = await postAction({ action: 'operateur_evenements', depuis })
    if (json?.ok === false) return ko(json?.motif || "Les annonces n'ont pas pu être lues")
    return {
      ok: true,
      data: {
        evenements: Array.isArray(json?.evenements) ? json.evenements : [],
        jusqua: json?.jusqua || null,
        total: Number(json?.total) || 0,
        // ⚠️ CE QUE LE SERVEUR A COUPÉ DOIT ARRIVER JUSQU'À L'ÉCRAN. Il le
        // disait déjà (`coupes`), et personne ne le lisait : un plafond muet
        // fait croire à douze événements quand il y en a quarante. Ce sont
        // des TRAITS uniquement — ce qui presse n'est plus jamais coupé.
        coupes: Number(json?.coupes) || 0,
      },
      motif: null,
    }
  } catch (e) {
    console.error('[accueil] évènements', e?.message || e)
    return ko("Les annonces de l'opérateur n'ont pas pu être lues")
  }
}
